import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { Context, Tokenomics, TokenomicsField } from '@/components/Context'
import ImageUploading from 'react-images-uploading'
import Button from '@/components/Button'
import { uni1d800 } from '@/utils/extras'
import Loader from '../Loader'
import LoadImageOnDemand from '@/components/LoadImageOnDemand'
import TokenomicsForm from '@/components/TokenomicsForm'
import { ArtState } from '@/database/types'
import { ArtData } from '@/database/art'

export const MINIMAL_FEE_PERCENTAGE = 5

export const defaultTokenomics: Tokenomics = {
    artState: ArtState.CREATED,
    fee: MINIMAL_FEE_PERCENTAGE,
    owner: 0,
    creator: 10,
    burnToken: '',
    airdrop: 5,
    votes: 40,
    claims: 30,
    treasury: 5,
    burn: 5,
    burnAddress: '',
    creatorAddress: '',
    treasuryAddress: '',
}

const CreateOverview = (): ReactElement => {
    const { accountSignature, setActiveUpdates, currentAccount, categories } = useContext(Context)
    const logoRef = useRef(null)

    const [title, setTitle] = useState('')
    const [transferFee, setTransferFee] = useState(0)
    const [description, setDescription] = useState('')
    const [url, setURL] = useState('')
    const [message, setMessage] = useState('')
    const [category, setCategory] = useState('Art')
    const [isWaiting, setIsWaiting] = useState(false)
    const [artData, setArtDataData] = useState<ArtData>()
    const [images, setImages] = React.useState([])
    const [tokenomics, setTokenomics] = React.useState(defaultTokenomics)

    const tokenomicsTotal: number = Object.keys(tokenomics)
        .filter(
            (key) =>
                key !== 'artState' &&
                key !== 'burnToken' &&
                key !== 'owner' &&
                key !== 'burnAddress' &&
                key !== 'creatorAddress' &&
                key !== 'treasuryAddress',
        )
        .reduce((acc, key) => acc + tokenomics[key as keyof TokenomicsField], 0)

    const maxNumber = 1
    const createArtHandler = async (e, onImageRemoveAll: Function, onImageUpload: Function) => {
        e.preventDefault()

        if (!accountSignature) {
            window.alert('Please connect first.')
            return
        }
        if (description === '') {
            window.alert('Please provide a description.')
            return
        }
        if (title === '') {
            window.alert('Please provide a title.')
            return
        }

        setIsWaiting(true)
        setMessage('Uploading CRAZY Art...')

        onImageRemoveAll()
        await onImageUpload()

        setIsWaiting(false)
        setMessage('')
    }

    const proposeHandler = async (event: { preventDefault: () => void }) => {
        event.preventDefault()

        if (!artData) {
            window.alert('Please upload art first.')
            return
        }

        const artId = {
            id: artData.art.id.toString(),
        }

        try {
            const response = await axios.post('/propose-mint/', {
                account: currentAccount,
                signature: accountSignature,
                id: artId,
            })
            setURL(response.data)
            window.location.hash = `#art-${artId.id}#mint#details`
        } catch (error) {
            console.error('Error proposing mint:', error)
        }
        console.log('Art stored at ' + url)
    }

    const uploadArt = async () => {
        const image_data: any = images[0]
        const newArt = {
            category: category,
            title: title,
            description: description,
            transferFee: transferFee,
            data: image_data.data_url,
            walletAddress: currentAccount,
            tokenomicsForcedAuction: tokenomics,
        }
        try {
            const response = await axios.post('/upload-art/', {
                account: currentAccount,
                signature: accountSignature,
                art: newArt,
            })
            const artData = response.data
            setArtDataData(artData)
        } catch (error) {
            console.error('Error uploading art:', error)
        }
    }

    const onChange = (imageList) => {
        if (!imageList || imageList.length === 0) return
        setImages(imageList)
    }

    useEffect(() => {
        if (!images || images.length === 0) return
        uploadArt().then(() => setImages([]))
    }, [images])

    const onError = (errors, files) => {
        console.log('Error', errors, files)
    }

    useEffect(() => {
        setActiveUpdates([])

        const adjustScale = () => {
            const pageWidth = window.innerWidth
            let scale: number

            if (pageWidth <= 400) {
                scale = 0.8
            } else if (pageWidth > 400 && pageWidth <= 750) {
                scale = 0.8 + (0.2 * (pageWidth - 400)) / (750 - 400)
            } else {
                scale = 1.0
            }

            if (logoRef.current) {
                // @ts-ignore
                logoRef.current.style.transform = `scale(${scale})`
            }
        }

        adjustScale()

        window.addEventListener('resize', adjustScale)

        return () => window.removeEventListener('resize', adjustScale)
    }, [])

    const handleSelectCategoryHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedOptions = Array.from(event.target.selectedOptions)
        for (const option of selectedOptions) {
            setCategory(option.value)
        }
    }

    return (
        <div>
            <h1 className="title">Contribute your CRAZY Art to the CRAZY NFT collection</h1>
            <div className="form margin-left">
                {(!artData?.art.id || !artData?.art.title) && (
                    <div className="artwork-box padding-five">
                        {/*// @ts-ignore*/}
                        <form onSubmit={createArtHandler}>
                            Category
                            <select
                                className={categories?.length ? 'white width100' : 'lighter-gray width100'}
                                required={true}
                                multiple={false}
                                onChange={handleSelectCategoryHandler}
                            >
                                {!categories?.length && (
                                    <option key={'first'} title={'first'}>
                                        {' - Select Category -'}
                                    </option>
                                )}
                                {categories.map((category) => (
                                    <option className={'white'} key={category} title={category} value={category}>
                                        {category}
                                    </option>
                                ))}
                            </select>
                            Title
                            <input
                                className="width335"
                                placeholder="CRAZY Art"
                                onChange={(e) => {
                                    setTitle(e.target.value)
                                }}
                            ></input>
                            <div>Describe your art</div>
                            <textarea
                                placeholder="... CRAZY Art Description ..."
                                onChange={(e) => {
                                    setDescription(e.target.value)
                                }}
                            ></textarea>
                            Minimal transfer fee in CRAZY
                            <input
                                className="width100"
                                type="number"
                                max={666}
                                min={0}
                                placeholder="0"
                                onChange={(e) => {
                                    const price = Number(e.target.value)
                                    setTransferFee(price > 0 ? price : 0)
                                }}
                            ></input>
                            <div className={'artwork-box margin-left centered-row margin-top-five'}>
                                <span className={'margin-left-ten nav__voted-amount'}>
                                    Initial Auction Tokenomics
                                    <TokenomicsForm tokenomics={defaultTokenomics} onChange={setTokenomics} />
                                </span>
                            </div>
                        </form>
                        <ImageUploading
                            multiple={false}
                            value={images}
                            onChange={onChange}
                            onError={onError}
                            maxNumber={maxNumber}
                            dataURLKey="data_url"
                        >
                            {({
                                imageList,
                                onImageUpload,
                                onImageRemoveAll,
                                onImageUpdate,
                                onImageRemove,
                                isDragging,
                                dragProps,
                                errors,
                            }) => (
                                <div>
                                    <div className={'menu-wrapper-column menu-wrapper'}>
                                        <Button
                                            btnName="Upload Your Art"
                                            classStyles={'nav__medium long-button'}
                                            handleClick={(event: any) => {
                                                createArtHandler(event, onImageRemoveAll, onImageUpload).then()
                                            }}
                                            disabled={
                                                !accountSignature ||
                                                !currentAccount ||
                                                !title ||
                                                !description ||
                                                tokenomicsTotal !== 100
                                            }
                                            cancelBg={undefined}
                                        ></Button>
                                        {(!accountSignature || !currentAccount || !title || !description) && (
                                            <span className={'deep-red'}>
                                                Connect your wallet + <br></br> Fill in a title, description and
                                                tokenomics first
                                            </span>
                                        )}
                                    </div>
                                    {errors && (
                                        <div>
                                            {errors.maxNumber && (
                                                <span>Number of selected images exceed maxNumber</span>
                                            )}
                                            {errors.acceptType && <span>Your selected file type is not allow</span>}
                                            {errors.maxFileSize && <span>Selected file size exceed maxFileSize</span>}
                                            {errors.resolution && (
                                                <span>Selected file is not match your desired resolution</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </ImageUploading>
                    </div>
                )}
                <div ref={logoRef} className="scale_logo react-flow__shadow_blue">
                    <div className="react-flow__shadow_red">
                        <div className="image">
                            <div className="react-flow-image-holder">
                                <div className="react-flow-image-holder__node react-flow-image-holder__node-turbo nopan selectable">
                                    <div className="wrapper gradient">
                                        <div className="inner">
                                            <div className="body">
                                                <div>
                                                    {!isWaiting && artData?.image.id ? (
                                                        <LoadImageOnDemand imageId={artData.image.id} alt="Your Art" />
                                                    ) : isWaiting ? (
                                                        <Loader />
                                                    ) : (
                                                        <></>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={'menu-wrapper-column'}>
                    <div className="artwork-box">
                        {artData?.image?.id && (
                            <div>
                                <div className={'header-container'}>
                                    <div className="nav__balance menu-wrapper-column border margin-box">
                                        <span className="nav__voted-amount">#{artData?.art.id}</span>
                                        <span className="gray bold small">Art</span>
                                    </div>
                                </div>

                                <div className={'header-container'}>
                                    <div className="nav__balance menu-wrapper-column border margin-box">
                                        <span className="nav__voted-amount">{artData?.art.title}</span>
                                        <span className="gray bold small">Title</span>
                                    </div>
                                </div>

                                <div className={'header-container'}>
                                    <div className="nav__balance menu-wrapper-column border margin-box">
                                        <span className="nav__voted-amount">{artData?.art.prompt}</span>
                                        <span className="gray bold small">Description</span>
                                    </div>
                                </div>

                                <div className={'header-container'}>
                                    <div className="nav__balance menu-wrapper-column border margin-box">
                                        <span className={uni1d800.className + ' white'}>
                                            \uD836\uDE17
                                            {artData?.art.transferFee}
                                        </span>
                                        <span className="gray bold small">Transfer Fee</span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className={'menu-wrapper-column'}>
                            {/*// @ts-ignore*/}
                            <Button
                                btnName="Propose NFT Mint"
                                classStyles={'nav__medium long-button'}
                                handleClick={(event: any) => {
                                    proposeHandler(event).then()
                                }}
                                disabled={
                                    !(accountSignature && currentAccount && artData?.art.id && artData?.art.title)
                                }
                                cancelBg={undefined}
                            ></Button>
                            {!(accountSignature && currentAccount && artData?.art.id && artData?.art.title) && (
                                <span className={'deep-red'}>Upload Your Art first</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {isWaiting && <Loader />}
        </div>
    )
}

export default CreateOverview
